import { Injectable } from "@angular/core";
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router } from "@angular/router";
import { APPLICATION_TYPES, USER_MGMT_CNST } from "../constants/proj.cnst";
import { AuthenticationService } from "../modules/authentication/services/authentication.service";
import { AppStateService } from "../services/app-state.service";
import { CsrfTokenService } from "../services/csrf-token.service";
import { DataService } from "../services/data.service";
import { EnvService } from "../services/env.service";
import { HttpService } from "../services/http.service";
import { UtilsService } from '../services/utils.service';
import { TokenService } from "../services/token.service";
import { CommonService } from "../services/common.service";
import { PROJECT_CONSTANTS } from "../constants/proj.cnst";
import { isNullOrUndefined } from "../shared/common-utils";
import { KeycloakService } from "../services/keycloak.service";

@Injectable({
  providedIn: "root"
})
export class AuthGuard implements CanActivate {

  userPermissions: any;
  filteredPermissions: any = {};
  loggedInUserTypes = USER_MGMT_CNST.LOGGED_IN_USER_TYPES;
  applications = APPLICATION_TYPES;
  isOneOfPredefinedResources: boolean = false;

  constructor(
    protected readonly router: Router,
    private utilSrv: UtilsService,
    private authSrv: AuthenticationService,
    private appStateSrv: AppStateService,
    private httpSrv: HttpService,
    private dataSrv: DataService,
    private csrfTokenSrv: CsrfTokenService,
    private envService: EnvService,
    private tokenService:TokenService,
    protected readonly keyCloakSrv: KeycloakService,
    private appState: AppStateService,
    private commonserv: CommonService,
  ) { }

  async canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {
    const loggedIn = this.authSrv.isLoggedIn() || this.keyCloakSrv.isLoggedIn();
    const resource = next.data["resource"] || '';

    if (!loggedIn) {
      return this.handleUnAuthorizedAccess("Please login to view requested page.", ['username']);
    }
  
    const accessToken = await this.authSrv.getAccessToken();

    // Decode and validate the token
    if (!accessToken || this.tokenService.isTokenExpired(accessToken)) {
      let clearOutKeysFromLocalStorage = ['csrfToken', 'customerInfo', 'loggedIn', 'loggedInUserType', 'metaInfo', 'token', 'token', 'userInfo', 'userPermissions'];
      clearOutKeysFromLocalStorage.forEach(key => {
        localStorage.removeItem(key);
      });
      return this.handleUnAuthorizedAccess("Session expired. Please log in again.", ['username']);
    };

    const tokenValidated = await this.validateCsrfToken(accessToken);
    if (!tokenValidated) {
      return this.handleUnAuthorizedAccess("Please login to view requested page.", ['username']);
    };

    let userId;
    if(this.keyCloakSrv.isLoggedIn()) {
      const userProfile = await this.appStateSrv.getUserInfo();
      userId = userProfile.id;
    } else {
      userId = this.appStateSrv.getUserInfoFromLocalStorage()?.userId;
    }
    if (!userId) {
      return this.handleUnAuthorizedAccess("User ID is missing.", ['login']);
    }
    const hasPermissionsLoaded = this.appStateSrv.getPermissionsLoaded();
    const isPermissionRequestInProcess = this.appStateSrv.getInProcess();
    this.userPermissions = this.appStateSrv.getUserPermissions();

    if (hasPermissionsLoaded) {
      return this.checkPermissions(resource);
    } else if (isPermissionRequestInProcess) {
      return this.registerCallBack(resource);
    } else {
      return this.fetchUserPermissions(resource, userId);
    }
  }
  

  private async validateCsrfToken(accessToken: string): Promise<boolean> {
    if (!isNullOrUndefined(accessToken)) {
      if (!this.csrfTokenSrv.doesCsrfTokenExist()) {
        const response = await this.csrfTokenSrv.generateCsrfToken(accessToken);
        if (Object.keys(response).length === 0) {
          return false;
        }
        this.setCsrfToken(response);
        const customerDetails = {
          customerId: response['response']['userDetails']['customerId'],
          customerName: response['response']['userDetails']['customerName']
        };
        localStorage.setItem('customerInfo', JSON.stringify(customerDetails));
        this.dataSrv.setLoggedInUserInfo(response);
      }
      return true;
    }
    return false;
  }

  private fetchUserPermissions(resource: string, userId: any): Promise<boolean | UrlTree> {
    console.log("Fetching user permissions from login component");
    this.appStateSrv.setInProcess(true);
    const options = { uriParams: { userId } };
    

    return new Promise((resolve, reject) => {
      this.httpSrv.makeGetApiCall('GET_USER_PERMISSIONS', this.envService.baseUrl, options).subscribe({
        next: (res: any) => {
          if (!isNullOrUndefined(res.response)) {
            this.processUserPermissions(res.response, resource);
            resolve(this.checkPermissions(resource));
          } else {
            resolve(this.handleUnAuthorizedAccess());
          }
        },
        complete: () => {},
        error: () => {
          resolve(this.handleUnAuthorizedAccess());
        }
      });
    });
  }

  private processUserPermissions(response: any, resource: string): void {
    this.appStateSrv.setInProcess(false);
    this.appStateSrv.setPermissionsLoaded(true);
    this.userPermissions = response?.permissions ? response.permissions : {};
    this.appStateSrv.setLoggedInUserType(response);
    const metaInfo = response?.meta ? response.meta : {};
    this.appStateSrv.setUserMetaInfo(metaInfo);
    this.appStateSrv.setUserPermissionsRef(this.userPermissions);
    this.appStateSrv.setIsTokensLoaded(true);
    this.utilSrv.setUserPermissionsInLocalStorage(this.userPermissions, metaInfo);
  }

  private setCsrfToken(csrfTokenResponse: any): void {
    const { response } = csrfTokenResponse;
    if (!isNullOrUndefined(response)) {
      const { tokens } = response;
      if (!isNullOrUndefined(tokens)) {
        const { csrf } = tokens;
        if (isNullOrUndefined(localStorage.getItem('csrfToken'))) {
          localStorage.setItem('csrfToken', csrf);
        }
      }
    }
  }

  private handleUnAuthorizedAccess(message: string = "You don't have permissions to view the requested resource", route: string[] = ['', 'errors', 'access-denied']) {
    this.router.navigate(['/username'])
    this.utilSrv.showToastMessage(message, "error");
    return false;
  }

  private registerCallBack(resource: string): Promise<boolean | UrlTree> {
    return new Promise((resolve) => {
      const permissionsRef = this.appStateSrv.userPermissionsRef.subscribe((response: any) => {
        this.userPermissions = response;
        permissionsRef.unsubscribe();
        resolve(this.checkPermissions(resource));
      });
    });
  }

  private checkPermissions(resource: string): boolean | UrlTree {
    if (resource == USER_MGMT_CNST.LANDING_PAGE_RESOURCE) {
      this.routingBasedOnPermissions();
      return true;
    } else {
      const appName = USER_MGMT_CNST.CURRENT_USERMANAGEMENT_APPLICATION;
      this.appStateSrv.setCurrentResourceName(resource);

      if (!resource) return true;

      if (!isNullOrUndefined(appName)) {
        const hasResourceAccess = this.doesUserHasResourceAccess(resource);

        if (hasResourceAccess) {
          return true;
        }

        const nextResource = this.getRedirectionRoute();
        console.log("Next Resource:: ", nextResource);

        this.router.navigate(nextResource);
        return true;
      }

      return false;
    }
  }

  private doesUserHasResourceAccess(resourceName: string): boolean {
    if (!this.userPermissions) return false;

    const currentApplicationPermissions = this.userPermissions?.[USER_MGMT_CNST.CURRENT_USERMANAGEMENT_APPLICATION] || { resources: {} };

    return resourceName in currentApplicationPermissions.resources;
  }

  private getRedirectionRoute(): string[] {
    const routeResourceMapping = {
      'USER': ['', 'users'],
      'APPLICATION': ['', 'applications'],
      'RESOURCE': ['', 'resources'],
      'ROLE': ['', 'roles']
    };

    const resourceOrder = {
      'USER': 1,
      'ROLE': 2,
      'APPLICATION': 3,
      'RESOURCE': 4
    };

    if (!this.userPermissions) {
      return ['', 'errors', 'access-denied'];
    }

    const resources = this.userPermissions?.[USER_MGMT_CNST.CURRENT_USERMANAGEMENT_APPLICATION]?.resources || {};
    let resourceList = Object.keys(resources).sort((a, b) => {
      if (!resourceOrder[a as keyof typeof resourceOrder] && resourceOrder[b as keyof typeof resourceOrder]) {
        return 1;
      }

      if (resourceOrder[a as keyof typeof resourceOrder] && !resourceOrder[b as keyof typeof resourceOrder]) {
        return -1;
      }

      if (!resourceOrder[a as keyof typeof resourceOrder] && !resourceOrder[b as keyof typeof resourceOrder]) return 0;
      return resourceOrder[a as keyof typeof resourceOrder] - resourceOrder[b as keyof typeof resourceOrder];
    });

    const nextResource = resourceList.length ? resourceList[0] : null;

    if (!nextResource) return ['', 'access-denied'];

    const routeForResource = routeResourceMapping[nextResource as keyof typeof routeResourceMapping] || ['', 'errors', 'access-denied'];
    return routeForResource;
  }

  private routingBasedOnPermissions() {
    this.userPermissions = this.appState.getUserPermissions();
    this.parseApplicationDetails();
    this.filterPermissions();
    const usermgmtAppName = PROJECT_CONSTANTS.USERMGMT_APP_NAME;
    const userType = this.appState.getLoggedInUserType();
    const permissionsLength = Object.keys(this.filteredPermissions).length;
    if (this.utilSrv.isObjectNotNullAndEmpty(this.userPermissions)) {
      if (Object.keys(this.userPermissions).length > 0) {
        if (userType === this.loggedInUserTypes.superAdmin) {
          this.router.navigate(['home']);
          return true;
        }
        if (userType === this.loggedInUserTypes.organizationUser && permissionsLength <= 1) {
          let appData: any = Object.values(this.filteredPermissions)[0];
          if (appData?.roleName == USER_MGMT_CNST.FIELD_ENGINEER || appData?.roleName == USER_MGMT_CNST.HR) {
            this.router.navigate(['/user-activity'], {
              queryParams: {
                applicationId: appData.applicationId,
              },
              queryParamsHandling: 'merge',
            })
          }
          else {
            if (appData?.applicationDetailsJson?.application_type == this.applications.OTHER) {
              this.commonserv.navigateToCustomApp(appData);
            } else {
              this.router.navigate(['ticket-dashboard']);
            }
          }
          return true;
        }
        if (userType === this.loggedInUserTypes.organizationAdmin && permissionsLength === 0) {
          this.router.navigate(['home', 'app']);
          return true;
        }
      }
      else if (Object.keys(this.userPermissions).includes(usermgmtAppName)) {
        this.router.navigate(['usermgmt', 'applications']);
      }
      else {
        this.router.navigate(['', 'access-denied']);
      }
    }
    else {
      console.log("user permissions is empty, unable to redirect");
      this.utilSrv.showToastMessage("User does not have permissions to view the application", "error");
      this.router.navigate(['', 'access-denied']);
    }
    return false;
  }

  filterPermissions() {
    this.filteredPermissions = structuredClone(this.userPermissions);
    delete this.filteredPermissions[PROJECT_CONSTANTS.APPLICATION_NAME];
    delete this.filteredPermissions[PROJECT_CONSTANTS.USERMGMT_APP_NAME];
  }

  parseApplicationDetails() {
    Object.keys(this.userPermissions).forEach(appKey => {
      const appDetailsJson = this.userPermissions[appKey].applicationDetailsJson;
      this.userPermissions[appKey].applicationDetailsJson =
        typeof appDetailsJson === 'string'
          ? JSON.parse(appDetailsJson)
          : appDetailsJson || {};
    });
  }
}
